import React, { useEffect, useState, useMemo } from 'react';
import { PlusCircle, Trash2, X, ChevronUp, ChevronDown, FileText, Receipt, ExternalLink, FileQuestion} from 'lucide-react';
import { useCostosModel } from '../../../modelos/Costos/modeloCostos';
import { useConfiguracion } from '../../../metodos/ConfiguracionContext';
import { formatearFechaCorta, formatearPrecio,formatearFechaCompleta } from '../../../utils/utils';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useFormOptions } from '../../../config/constants';
import FileUploader from '../../../components/FileUploader';
import EditableField from '../../../components/EditableFields';
import { useContabilidadModel } from '../../../modelos/Contabilidad/modeloContabilidad';
import { useDialog } from '../../../components/Dialogs/Dialogprovider';

// --- COMPONENTE PRINCIPAL ---
const ModuloCostos = () => {
  const{
    configuracion
  } = useConfiguracion()
  const {
    costos,
    loadingCostos,
    errorCostos,
    cargarCostos,
    setupWebSocketsCostos,

    cargarListaConceptos,
    listaConceptosOptions,
    cargarListaProveedores,
    listaProveedoresOptions,

    addNewCosto,
    updateCosto,
  } = useCostosModel();
  const {
    cuentasBancariasListOptions
  } = useFormOptions();
  const {
    searchArchivoDrive,
  } = useContabilidadModel();

  const dialogs = useDialog();
  

  // --- ESTADO PARA FILTROS ---
  const [filtroMes, setFiltroMes] = useState(''); // Formato "YYYY-MM"
  const [filtroProveedor, setFiltroProveedor] = useState('');

  const [estadosArchivos, setEstadosArchivos] = useState({});

  //filtros
  const costosFiltradosYOrdenados = useMemo(() => {
    let costosProcesados = [...costos];

    // 1. Filtrar por proveedor (sin cambios)
    if (filtroProveedor) {
      costosProcesados = costosProcesados.filter(costo =>
        costo.proveedor?.toLowerCase().includes(filtroProveedor.toLowerCase())
      );
    }

    // 2. Filtrar por mes y año (con la corrección)
    if (filtroMes) {
      costosProcesados = costosProcesados.filter(costo => {
        // 💡 CORRECCIÓN: Nos aseguramos de que la fecha sea un string antes de filtrar.
        if (!costo || !costo.fecha) return false; // Medida de seguridad

        // Si es un objeto Date, lo convertimos a string. Si ya es string, lo dejamos igual.
        const fechaComoString = (costo.fecha instanceof Date)
          ? costo.fecha.toISOString() 
          : String(costo.fecha);

        // Ahora sí podemos usar .startsWith() de forma segura.
        return fechaComoString.startsWith(filtroMes);
      });
    }

    // 3. Ordenar por fecha (sin cambios, new Date() maneja ambos tipos)
    costosProcesados.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

    return costosProcesados;
  }, [costos, filtroProveedor, filtroMes]);

  const mesesDisponibles = useMemo(() => {
    const meses = new Set(
        costos.map(costo => {
            // 💡 CORRECCIÓN: Nos aseguramos de que la fecha sea un string antes de usar .slice()
            if (!costo || !costo.fecha) return null; // Ignorar costos sin fecha

            // Si es un objeto Date, lo convertimos a string 'YYYY-MM-DD'
            // Si ya es un string, lo dejamos como está.
            const fechaComoString = (costo.fecha instanceof Date)
                ? costo.fecha.toISOString()
                : costo.fecha;
            
            return fechaComoString.slice(0, 7); // Extraer "YYYY-MM"
        }).filter(Boolean) // Elimina los valores nulos del array
    );
    return Array.from(meses).sort().reverse();
  }, [costos]);

  //cargar lista de archivos
  const verificarArchivosEnLotes = async (
    costos,
    // Función para reportar el progreso lote por lote
    onProgress,
    // Tamaño del lote (cuántas verificaciones hacer a la vez)
    batchSize = 10 
) => {
  if (!costos || costos.length === 0) return;

  // Creamos una copia para no modificar el original
  const costosAProcesar = [...costos];

  // Procesamos la lista en trozos del tamaño de batchSize
  for (let i = 0; i < costosAProcesar.length; i += batchSize) {
    // Obtenemos el lote actual
    const lote = costosAProcesar.slice(i, i + batchSize);
    
    const nuevosEstados = {};

    const promesas = lote.map(async (costo) => {
      const costoId = costo.documentoId;
      if (!costo.indexPago) {
        return; 
      }

      try {
        // La búsqueda en Drive sigue siendo la misma
        const resultado = await searchArchivoDrive(configuracion.nombreEmpresa, costo.indexPago);

        if (resultado && resultado.success) {
          nuevosEstados[costoId] = { status: 'found', data: resultado.data };
        } else {
          nuevosEstados[costoId] = { status: 'not_found' };
        }
      } catch (error) {
        nuevosEstados[costoId] = { status: 'not_found' };
      }
    });

    // Esperamos a que SOLO el lote actual termine
    await Promise.all(promesas);

    // Llamamos a la función de progreso para actualizar la UI
    if (Object.keys(nuevosEstados).length > 0) {
      onProgress(nuevosEstados);
    }
  }
  };

  useEffect(() => {
    // Si hay meses disponibles y el filtro aún no ha sido establecido por el usuario
    if (mesesDisponibles.length > 0 && !filtroMes) {
      // Establece el filtro en el mes más reciente (el primero de la lista ordenada)
      setFiltroMes(mesesDisponibles[0]);
    }
  }, [mesesDisponibles, filtroMes]); 

  useEffect(() => {
    // Función que se ejecutará solo cuando cambie la lista de costos
    const iniciarVerificacion = async () => {

      if (!filtroMes) {
        return;
      }

      if (costosFiltradosYOrdenados.length === 0) {
        // Limpiamos estados anteriores si la lista de costos se vacía
        setEstadosArchivos({});
        return;
      }

      // Inicializamos el estado: todos los archivos están 'pending' (pendientes)
      const estadoInicial = costosFiltradosYOrdenados.reduce((acc, costo) => {
        acc[costo.documentoId] = { status: 'pending' };
        return acc;
      }, {});
      setEstadosArchivos(estadoInicial);


      // Llamamos a nuestra nueva función por lotes
      await verificarArchivosEnLotes(costosFiltradosYOrdenados, (nuevosResultadosDelLote) => {
        // Cada vez que un lote termina, actualizamos el estado general
        // Usamos el formato de callback para asegurar que tenemos el estado más reciente
        setEstadosArchivos(estadoPrevio => ({
          ...estadoPrevio,
          ...nuevosResultadosDelLote,
        }));
      });
    };

    iniciarVerificacion();

    // IMPORTANTE: El arreglo de dependencias
    // Esto asegura que el efecto solo se ejecute cuando la lista de `costos` cambie.
  }, [costosFiltradosYOrdenados]);

  //cargar costos
  useEffect(() => {
    if (configuracion.nombreEmpresa) {
      cargarCostos(configuracion.nombreEmpresa);

      const cleanupWebSockets = setupWebSocketsCostos(configuracion.nombreEmpresa);

      return () =>{
        cleanupWebSockets();
      };

    }
  }, [configuracion.nombreEmpresa]);
  

  //cargar lista de proveedores y conceptos
  useEffect(() =>{
    if (configuracion.nombreEmpresa) {
      cargarListaConceptos(configuracion.nombreEmpresa);
      cargarListaProveedores(configuracion.nombreEmpresa);
    }
   
  },[configuracion.nombreEmpresa]);

  // Estado para manejar los valores del formulario
  const [nuevoCosto, setNuevoCosto] = useState({
    concepto: '',
    descripcion: '',
    valor: 0,
    proveedor: '',
    formaPago: '',
    referencia: '',
    fecha: new Date(),

    archivoPago: null,
    archivoFactura: null,
  });
  
  // Manejador para actualizar el estado del formulario en cada cambio
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNuevoCosto({ ...nuevoCosto, [name]: value });
  };
  
  // Manejador para eliminar un costo (requiere confirmación)
  const handleDelete = (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este costo?')) {
    }
  };

  const ResumenCostos = ({ costos }) => {
    // 💡 PASO 1: Estado para controlar si el desglose está abierto o cerrado.
    const [estaDesglosado, setEstaDesglosado] = useState(false);

    // La lógica para calcular las estadísticas no cambia, sigue siendo eficiente.
    const estadisticas = useMemo(() => {
      const costoTotal = costos.reduce((sum, costo) => sum + costo.valor, 0);
      const costosPorCategoria = costos.reduce((acc, costo) => {
        const categoria = costo.concepto || 'Sin Categoría';
        if (!acc[categoria]) {
          acc[categoria] = 0;
        }
        acc[categoria] += costo.valor;
        return acc;
      }, {});
      
      const categoriasArray = Object.entries(costosPorCategoria)
        .map(([nombre, total]) => ({ nombre, total }))
        .sort((a, b) => b.total - a.total);

      return { costoTotal, categorias: categoriasArray };
    }, [costos]);

    // El diccionario de colores se mantiene igual.
    const COLORES_CATEGORIA = {
      'WHATSAPP API': 'bg-green-500',
      'GOOGLE WORKSPACE': 'bg-yellow-500',
      'DATOS CELULAR': 'bg-red-500',
      'COLABORADORES': 'bg-blue-500',
      'EQUIPO': 'bg-indigo-500',
      'INTERNET': 'bg-purple-500',
      'GOOGLE CLOUD': 'bg-pink-500',
      'SERVIDOR': 'bg-gray-500',
      'DEFAULT': 'bg-gray-400',
    };

    return (
      <div className="mb-6 p-4 bg-white rounded-xl shadow-sm">
        <h2 className="text-lg font-bold text-gray-800 mb-3">Resumen de Costos</h2>
        
        {/* --- VISTA PRINCIPAL (COMPACTA) --- */}
        <div className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
          <span className="font-semibold text-gray-600">COSTO TOTAL</span>
          <div className="flex items-center gap-4">
            <span className="text-xl font-bold text-blue-600">
              {formatearPrecio(estadisticas.costoTotal)}
            </span>
            {/* 💡 PASO 2: Botón para mostrar/ocultar el desglose */}
            <button 
              onClick={() => setEstaDesglosado(!estaDesglosado)}
              className="p-1 text-gray-500 hover:text-blue-600 hover:bg-gray-200 rounded-full"
              aria-expanded={estaDesglosado}
            >
              {estaDesglosado ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>
        </div>

        {/* 💡 PASO 3: El desglose solo se muestra si el estado es 'true' */}
        {estaDesglosado && (
          <div className="mt-4 space-y-2 animate-fade-in-down">
            {estadisticas.categorias.map(({ nombre, total }) => (
              <div key={nombre} className="flex justify-between items-center p-2 border-b border-gray-100 last:border-b-0">
                <div className="flex items-center gap-3">
                  <span 
                    className={`w-2.5 h-2.5 rounded-full ${COLORES_CATEGORIA[nombre] || COLORES_CATEGORIA['DEFAULT']}`}
                  ></span>
                  <span className="text-sm font-medium text-gray-500">{nombre}</span>
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  {formatearPrecio(total)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const formatearMesAno = (fechaString) => {
    // 1. Si no hay fecha, retorna un string vacío.
    if (!fechaString || fechaString.length !== 7) return "";

    // 2. Separa el año y el mes del string.
    const [year, month] = fechaString.split('-');

    // 3. Crea un objeto Date. El mes en el constructor de Date es 0-indexado (0=enero), por eso restamos 1.
    const fechaObj = new Date(year, month - 1);

    // 4. Usa el formateador de JavaScript para el idioma español.
    const formateador = new Intl.DateTimeFormat('es-CO', {
      month: 'long', // 'long' para "julio", 'short' para "jul."
      year: 'numeric',
    });

    // 5. Formatea la fecha y capitaliza la primera letra para un mejor look.
    const fechaFormateada = formateador.format(fechaObj);
    return fechaFormateada.charAt(0).toUpperCase() + fechaFormateada.slice(1);
  };

  const handleSave = (field, value, documentId) =>{

    updateCosto(documentId,configuracion.nombreEmpresa,field,value,'field');

  };

  // --- COMPONENTE PARA LA TARJETA DE UN COSTO ---
  const CardCosto = ({ costo}) => {
    const estadoArchivo = estadosArchivos[costo.documentoId];

    return (
      <div 
        key={costo.documentoId} 
        className="bg-white p-5 rounded-xl shadow-sm border-l-4 border-blue-500 transition-transform hover:scale-[1.01]"
      >
        {/* Cabecera de la tarjeta */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="text-sm font-semibold text-blue-800 uppercase">{costo.concepto}</p>
            <p className="text-xs text-gray-500">{costo.documentoId}</p>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={async() => {
                //valor en 0
                await handleSave('valor',0,costo.documentoId);

                //fecha en 2023,1,1
                const fechaEspecifica = new Date(2023, 0, 1);
                await handleSave('fecha',fechaEspecifica,costo.documentoId);
              }} 
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-100 rounded-full transition-colors"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>

        {/* Cuerpo de la tarjeta */}
        <div className="space-y-2 text-sm">

          <div className="flex justify-between items-center bg-slate-50 p-2 rounded-md">
            <span className="font-medium text-gray-600">Concepto:</span>
            <EditableField
              field="concepto"
              label="Concepto"
              value={costo.concepto}
              type="react-select"
              options={listaConceptosOptions}
              isAdmin={true}
              onSave={(field, value) => {
                handleSave(field, value, costo.documentoId); 
              }}
            />
          </div>

          <div className="flex justify-between items-center bg-slate-50 p-2 rounded-md">
            <span className="font-medium text-gray-600">Valor Pago:</span>
            <EditableField
              field="valor"
              label="Valor de pago"
              value={costo.valor}
              type="number"
              isAdmin={true}
              onSave={(field, value) => {
                handleSave(field, value, costo.documentoId); 
              }}
            />
          </div>

          <div className="flex justify-between items-center bg-slate-50 p-2 rounded-md">
            <span className="font-medium text-gray-600">Fecha Pago:</span>
            <EditableField
              field="fecha"
              label="Valor de pago"
              value={costo.fecha}
              type="date"
              isAdmin={true}
              onSave={(field, value) => {
                handleSave(field, value, costo.documentoId); 
              }}
            />
          </div>

          <div className="flex justify-between items-center bg-slate-50 p-2 rounded-md">
            <span className="font-medium text-gray-600">Descripción:</span>
            <span className="text-gray-700 truncate" title={costo.descripcion}>
              <EditableField
                field="descripcion"
                label="Descripcion"
                value={costo.descripcion}
                type="text"
                isAdmin={true}
                onSave={(field, value) => {
                handleSave(field, value, costo.documentoId); 
              }}
              />
            </span>
          </div>

          <div className="flex justify-between items-center bg-slate-50 p-2 rounded-md">
            <span className="font-medium text-gray-600">Proveedor:</span>
            <EditableField
                field="proveedor"
                label="Proveedor"
                value={costo.proveedor}
                type="react-select"
                options={listaProveedoresOptions}
                isAdmin={true}
                onSave={(field, value) => {
                handleSave(field, value, costo.documentoId); 
              }}
              />
          </div>

          <h3 className="font-semibold text-gray-700">Archivos Adjuntos</h3>

          {/* Contenedor para los botones de archivo */}
          <div className="flex flex-col sm:flex-row gap-3">
              
              {/* --- Botón para la Factura --- */}
              {costo.linkFactura ? (
                  <a
                      href={costo.linkFactura}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                      <FileText size={18} /> {/* Ícono de factura/documento */}
                      Ver Factura
                  </a>
              ) : (
                  <div className="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium text-gray-400 bg-gray-200 rounded-md cursor-not-allowed">
                      <FileQuestion size={18} /> {/* Ícono para indicar ausencia */}
                      Sin Factura
                  </div>
              )}

              {/* --- Botón para el Comprobante de Pago --- */}
              {costo.linkComprobante ? (
                  <a
                      href={costo.linkComprobante}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                      <Receipt size={18} /> {/* Ícono de recibo/comprobante */}
                      Ver Comprobante
                  </a>
              ) : (
                  <div className="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium text-gray-400 bg-gray-200 rounded-md cursor-not-allowed">
                      <FileQuestion size={18} /> {/* Ícono para indicar ausencia */}
                      Sin Comprobante
                  </div>
              )}
          </div>

          {costo.indexPago && (
            <>
              <p className="text-xs text-gray-500 mb-2 truncate">
                  Archivo: <span className="font-medium">{costo.indexPago}</span>
              </p>

              {/* 💡 --- ESTE ES EL BLOQUE CORREGIDO --- 💡 */}
              <div className="my-2 p-2 rounded-md text-sm font-semibold text-center">
                  
                  {/* Cuando el estado es 'pending', muestra un mensaje de carga */}
                  {estadoArchivo?.status === 'pending' && (
                      <p className="text-gray-500 animate-pulse">Verificando archivo...</p>
                  )}
                  
                  {/* Cuando el estado es 'found', muestra el enlace */}
                  {estadoArchivo?.status === 'found' && (
                      <a
                          href={estadoArchivo.data.webViewLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block text-green-600 hover:text-green-800 transition-colors"
                      >
                          ✅ Archivo Encontrado
                      </a>
                  )}

                  {/* Cuando el estado es 'not_found', muestra el error */}
                  {estadoArchivo?.status === 'not_found' && (
                      <p className="text-red-600">❌ Archivo No Encontrado</p>
                  )}
              </div>
            </>
          )}

        </div>
      </div>
    );
  };

  const handleSubmit = async(e) =>{
    e.preventDefault();

    try {

      const fechaNueva = new Date(nuevoCosto.fecha);
      const fechaActual = new Date();
    
      if (fechaNueva < fechaActual) {
    
        const onAddCosto = async() => {
          await addNewCosto(configuracion.nombreEmpresa, nuevoCosto);
          console.log("nuevo costo",nuevoCosto);
          dialogs.showSuccess('Fecha de entrega actualizada correctamente.');
        };
    
          dialogs.showConfirm(
            `Estas registrando un costo a una fecha pasada ${formatearFechaCompleta(fechaNueva)}, estas seguro?`,
            onAddCosto, // Callback simple
            'Advertencia de fecha' // Título
          );
              
              return;
    
      }

     
     
      // Limpiar formulario y cerrar modal después del éxito
      resetForm();
    } catch (error) {
      console.error('Error al añadir el costo:', error);
    }

  }

  const resetForm = () =>{
    setNuevoCosto({
      concepto: '',
      descripcion: '',
      valor: 0,
      proveedor: '',
      formaPago: '',
      referencia: '',
      fecha: new Date().toISOString().slice(0, 10),

      archivoPago: null,
      archivoFactura: null,
    });
  }

  const getOptionFromValue = (options, value) => {
    if (!value || !options || options.length === 0) return null;
    return options.find(option => option.value === value) || null;
  };

  //funcion de select
  const handleSelectChange = (selectedOption, { name }) => {
    setNuevoCosto({
      ...nuevoCosto,
      [name]: selectedOption ? selectedOption.value : ''
    });

  };

  
  return (
    <div className="bg-slate-50 min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8">
        
        {/* --- FORMULARIO (IZQUIERDA) --- */}
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm h-fit">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Registrar Costos</h2>
          
          {/* El evento onSubmit en el form llama a tu función de registro */}
          <form onSubmit={handleSubmit} className="space-y-5">
            
            {/*Concepto*/}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Concepto *
              </label>
              <CreatableSelect
                name="concepto"
                value={
                  nuevoCosto.concepto
                    ? { label: nuevoCosto.concepto, value: nuevoCosto.concepto }
                    : null
                }
                onChange={handleSelectChange}
                options={listaConceptosOptions}
                placeholder="Selecciona el tipo de servicio"
                isClearable
                isSearchable
                classNamePrefix="react-select"
                required
              />
            </div>

            {/*Descripcion*/}
            <div>
              <label htmlFor="descripcion" className="block text-sm font-medium text-gray-600 mb-1">Descripción</label>
              <input 
                type="text" 
                name="descripcion" 
                value={nuevoCosto.descripcion} 
                onChange={(e) => setNuevoCosto({...nuevoCosto, descripcion: e.target.value})}
                placeholder="Detalles del costo" 
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required 
              />
            </div>
            
            {/*Valor*/}
            <div>
              <label htmlFor="valor" className="block text-sm font-medium text-gray-600 mb-1">Valor de pago</label>
              <input 
                type="number" 
                name="valor" 
                value={nuevoCosto.valor} 
                onChange={handleInputChange} 
                placeholder="0.00" 
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required 
              />
            </div>

            {/*Proveedor*/}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Proveedor *
              </label>
              <CreatableSelect
                name="proveedor"
                value={
                  nuevoCosto.proveedor
                    ? { label: nuevoCosto.proveedor, value: nuevoCosto.proveedor }
                    : null
                }
                onChange={handleSelectChange}
                options={listaProveedoresOptions}
                placeholder="Selecciona su proveedor"
                isClearable
                isSearchable
                classNamePrefix="react-select"
                required
              />
            </div>
            
            {/*Metodo de pago*/}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Metodo de pago *
              </label>
              <Select
                name="formaPago"
                value={getOptionFromValue(cuentasBancariasListOptions, nuevoCosto.formaPago)}
                onChange={handleSelectChange}
                options={cuentasBancariasListOptions}
                placeholder="Selecciona tu metodo de pago"
                isClearable
                isSearchable
                classNamePrefix="react-select"
                required
              />
            </div>

            {/*Referencia*/}
            <div>
              <label htmlFor="descripcion" className="block text-sm font-medium text-gray-600 mb-1">Referencia</label>
              <input 
                type="text" 
                name="referencia" 
                value={nuevoCosto.referencia} 
                onChange={(e) => setNuevoCosto({...nuevoCosto, referencia: e.target.value})}
                placeholder="Referencia de la transaccion" 
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required 
              />
            </div>
            
            {/*Fecha de pago*/}
            <div>
              <label htmlFor="fecha" className="block text-sm font-medium text-gray-600 mb-1">Fecha de pago</label>
              <input 
                type="datetime-local" 
                name="fecha" 
                value={nuevoCosto.fecha} 
                onChange={(e) => setNuevoCosto({...nuevoCosto, fecha: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* --- BOTONES DE ACCIÓN --- */}
            <div className="space-y-3 pt-4">

              {/* --- ARCHIVO DE PAGO --- */}
              <div>
                <label htmlFor="fecha" className="block text-sm font-medium text-gray-600 mb-1">Archivo de pago</label>
                <FileUploader 
                      formData={nuevoCosto}
                      setFormData={setNuevoCosto}
                      fieldName="archivoPago"
                      allowedTypes={[
                        'application/pdf',
                        'image/jpeg',
                        'image/jpg',
                        'image/png',
                        'image/gif',
                        'image/webp',
                        'image/bmp',
                        'image/svg+xml'
                      ]}
                      maxSize={10 * 1024 * 1024}
                      label="Archivo de pago"
                      multiple={false}
                    />
              </div>

              {/* --- ARCHIVO DE FACTURA --- */}
              <div>
                <label htmlFor="fecha" className="block text-sm font-medium text-gray-600 mb-1">Archivo de factura</label>
              <FileUploader 
                    formData={nuevoCosto}
                      setFormData={setNuevoCosto}
                      fieldName="archivoFactura"
                      allowedTypes={[
                        'application/pdf',
                        'image/jpeg',
                        'image/jpg',
                        'image/png',
                        'image/gif',
                        'image/webp',
                        'image/bmp',
                        'image/svg+xml'
                      ]}
                      maxSize={10 * 1024 * 1024}
                      label="Archivo de factura"
                    multiple={false}
              />
              </div>
            
               {/* --- REGISTRAR PAGO --- */}
              <button 
                type="submit" 
                className="w-full flex justify-center items-center gap-2 bg-blue-600 text-white font-bold p-3 rounded-lg hover:bg-blue-700 transition-colors shadow-md disabled:bg-gray-400"
                // disabled={loading} // Opcional: Deshabilitar mientras se envía
              >
                <PlusCircle size={20} />
                Registrar Pago
              </button>

            </div>

          </form>
        </div>

        {/* --- HISTORIAL (DERECHA) --- */}
        <div className="lg:col-span-3">

          <ResumenCostos costos={costosFiltradosYOrdenados} />

          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Historial</h2>
            <span className="font-semibold text-gray-500 bg-gray-200 px-3 py-1 rounded-full text-sm">
              {costosFiltradosYOrdenados.length} Registros
            </span>
          </div>
          
          {/* --- ÁREA DE FILTROS --- */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-white rounded-xl shadow-sm">
            {/* Filtro por Mes */}
            <div>
              <label htmlFor="filtro-mes" className="block text-sm font-medium text-gray-600 mb-1">Filtrar por Mes</label>
              <select 
                id="filtro-mes"
                value={filtroMes}
                onChange={(e) => setFiltroMes(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Todos los meses</option>
                {mesesDisponibles.map(mes => (
                  <option key={mes} value={mes}>
                    {/* 💡 CAMBIO CLAVE AQUÍ */}
                    {formatearMesAno(mes)}
                  </option>
                ))}
              </select>
            </div>
            {/* Filtro por Proveedor */}
            <div>
              <label htmlFor="filtro-proveedor" className="block text-sm font-medium text-gray-600 mb-1">Filtrar por Proveedor</label>
              <input 
                id="filtro-proveedor"
                type="text" 
                value={filtroProveedor}
                onChange={(e) => setFiltroProveedor(e.target.value)}
                placeholder="Buscar proveedor..."
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* --- LISTA DE COSTOS --- */}
          <div className="space-y-4">
            {loadingCostos && <p>Cargando costos...</p>}
            {errorCostos && <p className="text-red-500">{errorCostos}</p>}
            
            {!loadingCostos && costosFiltradosYOrdenados.map((costo) => (
              <CardCosto 
                key={costo.documentoId} 
                costo={costo} 
                onDelete={handleDelete}
                onEdit={handleInputChange}
              />
            ))}

            {!loadingCostos && costosFiltradosYOrdenados.length === 0 && (
              <div className="text-center py-10 px-4 bg-white rounded-lg shadow-sm">
                 <p className="text-gray-500">No se encontraron costos que coincidan con los filtros.</p>
                 <button 
                    onClick={() => { setFiltroMes(''); setFiltroProveedor(''); }}
                    className="mt-4 inline-flex items-center gap-2 bg-blue-100 text-blue-700 font-semibold px-4 py-2 rounded-lg hover:bg-blue-200"
                 >
                    <X size={16} />
                    Limpiar Filtros
                 </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

}

export default ModuloCostos;